import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { getApiErrorMessage } from '@/utils/apiErrors'

// Lets an individual query or mutation opt out of the global toast when it
// renders its failure inline instead.
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      suppressGlobalError?: boolean
    }
    mutationMeta: {
      suppressGlobalError?: boolean
    }
  }
}

type ErrorNotifier = (message: string) => void

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.'

let errorNotifier: ErrorNotifier | null = null

/**
 * Register the callback invoked whenever a query or mutation fails.
 *
 * Called by `QueryErrorNotifier` once it mounts inside `SnackbarProvider`. The
 * client is created here, outside React, so its identity — and therefore its
 * cache — stays stable across renders; that also means it cannot read context,
 * hence this seam.
 */
export const setQueryErrorNotifier = (notifier: ErrorNotifier | null): void => {
  errorNotifier = notifier
}

const notifyError = (error: unknown): void => {
  errorNotifier?.(getApiErrorMessage(error, DEFAULT_ERROR_MESSAGE))
}

/**
 * The app's shared TanStack Query client.
 *
 * Failures surface as a toast from here rather than from each call site, so a
 * failed read or write can never be silent. Retry and refetch behavior is left
 * at TanStack's defaults.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.suppressGlobalError) {
        return
      }
      notifyError(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.suppressGlobalError) {
        return
      }
      notifyError(error)
    },
  }),
})
